import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
} from "recharts";
import { Clock, Vote } from "lucide-react";

const SAAT_BICIM = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  hour: "2-digit",
  hourCycle: "h23",
});
const GUN_BICIM = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  day: "numeric",
  month: "long",
  weekday: "long",
});
// Türkiye saatine göre gün anahtarı: 2026-10-03
const GUN_ANAHTAR = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Istanbul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const gunFarki = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000);

// Bekleme animasyonu: tipik bir seçim gününün şekli (sabah yoğun, öğle
// sakin, akşamüstü ikinci tepe). Çubuklar sırayla inip kalkar, dalga gibi.
// Yalnız CSS transform/opacity kullanılır; telefonu yormaz.
const BEKLEME_CUBUKLARI = [34, 62, 88, 74, 46, 52, 68, 80, 58, 36];
const BEKLEME_STIL = `
@keyframes katilim-dalga {
  0%, 100% { transform: scaleY(0.42); opacity: 0.45; }
  50% { transform: scaleY(1); opacity: 1; }
}
@keyframes katilim-suzul {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.katilim-cubuk { transform-origin: bottom; animation: katilim-dalga 2.6s ease-in-out infinite; }
.katilim-sandik { animation: katilim-suzul 3s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .katilim-cubuk, .katilim-sandik { animation: none; }
}
`;

function Bekleme({ secimAt }) {
  let gun = "";
  try {
    gun = GUN_BICIM.format(new Date(secimAt));
  } catch {
    gun = "";
  }
  return (
    <>
      <style>{BEKLEME_STIL}</style>
      <div className="relative h-[132px] rounded-xl bg-gradient-to-b from-emerald-50/70 to-transparent dark:from-emerald-950/30 px-3 pt-3 overflow-hidden">
        <div className="katilim-sandik absolute right-3 top-3 h-8 w-8 rounded-lg bg-white/90 dark:bg-neutral-900/90 ring-1 ring-emerald-200 dark:ring-emerald-800 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-sm">
          <Vote className="h-4 w-4" />
        </div>
        <div className="flex items-end justify-between gap-1.5 h-[92px] border-b border-dashed border-emerald-300/70 dark:border-emerald-800">
          {BEKLEME_CUBUKLARI.map((h, i) => (
            <div key={i} className="flex-1 max-w-[26px] h-full flex items-end">
              <div
                className="katilim-cubuk w-full rounded-t-[4px] bg-gradient-to-t from-emerald-500 to-emerald-300 dark:from-emerald-600 dark:to-emerald-400"
                style={{ height: `${h}%`, animationDelay: `${i * 0.14}s` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between gap-1.5 mt-1">
          {BEKLEME_CUBUKLARI.map((_, i) => (
            <span key={i} className="flex-1 max-w-[26px] text-center text-[9.5px] tabular-nums text-neutral-400">
              {String(8 + i).padStart(2, "0")}
            </span>
          ))}
        </div>
      </div>
      <p className="text-[11.5px] text-neutral-500 dark:text-neutral-400 mt-2.5 leading-snug">
        {gun ? `${gun} ` : "Seçim günü "}oy kullananlar işaretlendikçe hangi saatte kaç kişinin geldiği
        burada görünecek.
      </p>
    </>
  );
}

/**
 * "Geldi" işaretlerinin saat saat dağılımı. Seçim gününden önce grafik yerine
 * bekleme animasyonu gösterilir; seçim günü yalnız o günün işaretleri çizilir
 * (önceden yapılmış deneme işaretleri grafiği karıştırmasın).
 *
 * Not: Recharts veri alanlarını SVG özniteliklerine yayar; alan adları
 * (saat, adet) bilerek öznitelik adlarıyla çakışmayacak şekilde seçildi.
 */
export default function KatilimGrafigi({ oylar, secimAt }) {
  const simdi = new Date();
  const bugunAnahtar = GUN_ANAHTAR.format(simdi);
  const secimAnahtar = useMemo(() => {
    const t = new Date(secimAt);
    return Number.isNaN(t.getTime()) ? null : GUN_ANAHTAR.format(t);
  }, [secimAt]);
  const bekliyor = Boolean(secimAnahtar) && bugunAnahtar < secimAnahtar;
  const kalanGun = bekliyor ? gunFarki(bugunAnahtar, secimAnahtar) : 0;

  const { veri, zirve, toplam } = useMemo(() => {
    const sayac = new Map();
    let adet = 0;
    for (const o of oylar.values()) {
      if (o.vote !== "geldi" || !o.vote_at) continue;
      const t = new Date(o.vote_at);
      if (Number.isNaN(t.getTime())) continue;
      if (secimAnahtar && GUN_ANAHTAR.format(t) !== secimAnahtar) continue;
      const s = Number(SAAT_BICIM.format(t));
      sayac.set(s, (sayac.get(s) || 0) + 1);
      adet += 1;
    }
    if (adet === 0) return { veri: [], zirve: null, toplam: 0 };
    const saatler = [...sayac.keys()];
    const bas = Math.min(8, ...saatler);
    const bit = Math.max(17, ...saatler);
    const satirlar = [];
    for (let s = bas; s <= bit; s++) {
      satirlar.push({ saat: `${String(s).padStart(2, "0")}:00`, adet: sayac.get(s) || 0 });
    }
    const tepe = satirlar.reduce((a, b) => (b.adet > (a?.adet ?? 0) ? b : a), null);
    return { veri: satirlar, zirve: tepe, toplam: adet };
  }, [oylar, secimAnahtar]);

  // İçinde bulunulan saat koyu çizilir; yalnız bugün seçim günüyse.
  const suAnkiSaat = bugunAnahtar === secimAnahtar ? `${SAAT_BICIM.format(simdi)}:00` : null;

  const baslik = bekliyor
    ? "Seçim gününü bekliyor"
    : toplam > 0
    ? `${toplam} kişi oy kullandı`
    : "Henüz işaretleme yok";

  return (
    <section
      className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-4 sm:p-5"
      data-testid="katilim-grafigi"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
            <Clock className="h-3 w-3" />
            Saat saat katılım
          </div>
          <div className="font-heading text-base sm:text-lg font-semibold text-neutral-950 dark:text-neutral-50 mt-0.5">
            {baslik}
          </div>
        </div>
        {/* Takvim günü farkı; üstteki sayaçla karışmasın diye "kaldı" değil "sonra" */}
        {bekliyor && kalanGun > 0 && (
          <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 ring-1 ring-emerald-200 dark:ring-emerald-800 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            {kalanGun === 1 ? "Yarın" : `${kalanGun} gün sonra`}
          </span>
        )}
        {!bekliyor && zirve && zirve.adet > 0 && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 dark:text-neutral-400">
              En yoğun saat
            </div>
            <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums">
              {zirve.saat} · {zirve.adet} kişi
            </div>
          </div>
        )}
      </div>

      {bekliyor ? (
        <Bekleme secimAt={secimAt} />
      ) : veri.length === 0 ? (
        <div className="h-[132px] rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700 flex items-center justify-center text-center px-6 text-xs text-neutral-500 dark:text-neutral-400">
          Sandıklar açıldı. Oy kullananlar "Geldi" olarak işaretlendikçe hangi saatte kaç kişinin
          geldiği burada görünecek.
        </div>
      ) : (
        <div className="h-[160px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={veri} margin={{ top: 18, right: 4, bottom: 0, left: -28 }}>
              <XAxis
                dataKey="saat"
                tick={{ fontSize: 10, fill: "#737373" }}
                tickLine={false}
                axisLine={{ stroke: "#E5E7EB" }}
                interval={0}
                tickFormatter={(v) => v.slice(0, 2)}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#A3A3A3" }}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(16,185,129,0.08)" }}
                formatter={(v) => [`${v} kişi`, "Oy kullanan"]}
                labelFormatter={(l) => `${l} – ${String(Number(l.slice(0, 2)) + 1).padStart(2, "0")}:00`}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E7EB" }}
              />
              {/* Animasyon kapalı: ekran 15 sn'de bir tazelendiğinde çubuklar
                  her seferinde sıfırdan büyüyüp kırpışıyordu. */}
              <Bar dataKey="adet" radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false}>
                {veri.map((d) => (
                  <Cell key={d.saat} fill={d.saat === suAnkiSaat ? "#047857" : "#10B981"} />
                ))}
                <LabelList
                  dataKey="adet"
                  position="top"
                  formatter={(v) => (v > 0 ? v : "")}
                  style={{ fontSize: 10, fill: "#525252", fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
